<template>
  <!-- Global drag overlay - shown when files are dragged anywhere on page -->
  <div v-if="isFileDraggedGlobally" class="drop-overlay" @dragover.prevent @drop="dropFile($event)"
    @dragleave="handleGlobalDragLeave">
    <div class="drop-overlay__content">
      <div class="drop-overlay__icon"></div>
      <div class="drop-overlay__text">{{ $t(`dropFile.${"dropHere"}`) || "Drop your file here" }}</div>
    </div>
  </div>

  <!-- Original drop zone component -->
  <div class="drop" :class="{ 'drop--over': isDragOver }" @dragenter="dragging($event)" @dragleave="dragLeave($event)"
    @dragover.prevent @drop="dropFile($event)">
    <div class="drop__icon" v-if="!files.length"></div>
    <div class="files" v-else>
      <div class="file" v-for="(file, index) in files" :key="index">
        <div class="file__type" :class="`file__type--${file.type}`"></div>
        <div class="file__name">
          {{ file.name }}
        </div>
        <div v-if="deletable" class="file__delete" @click="deleteFile(index)"></div>
      </div>
    </div>
    <div class="drop__title">
      {{ $t(`dropFile.${"title"}`) }}
    </div>

    <div v-if="disabled" class="disabled-el">
      <button type="button" disabled class="drop__button button button--default button--shadow">
        {{ $t(`dropFile.${"button"}`) }}
        <input type="file" class="drop__input" ref="inputFile" />
      </button>
    </div>
    <button v-else type="button" class="drop__button button button--default button--shadow"
      @click="openSingleFileDialog()">
      {{ $t(`dropFile.${"button"}`) }}
      <input type="file" class="drop__input" ref="inputFile" :multiple="multiple" @input="selectSingleFile($event)" />
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';

defineOptions({
  name: "DropFile"
});

const props = defineProps({
  deletable: {
    required: false,
    type: Boolean,
    default: true,
  },
  bus: {
    required: false,
    type: Object,
  },
  disabled: {
    required: false,
    type: Boolean,
    default: false,
  },
  multiple: {
    required: false,
    type: Boolean,
    default: false,
  },
  value: {
    required: false,
    type: Array,
    default: () => [],
  },
  modelValue: {
    required: false,
    type: Array,
    default: () => [],
  },
  showGlobalDropZone: {
    required: false,
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['input', 'update:modelValue']);

// Reactive States
const files = ref([]);
const isDragOver = ref(false);
const isFileDraggedGlobally = ref(false);
const inputFile = ref(null); // Bind to template ref="inputFile"
const dragCounter = ref(0); // Track nested drag events

const emitFiles = () => {
  emit("input", files.value);
  emit("update:modelValue", files.value);
};

// Helper Methods
const parseFileExtension = (name) => {
  const mimeTypeParts = name.split(".");
  let mimeType = mimeTypeParts[mimeTypeParts.length - 1].toLowerCase();
  if (mimeType === "jpg" || mimeType === "png" || mimeType === "svg") {
    mimeType = "image";
  }
  return mimeType;
};

// Check if dataTransfer contains files
const isFileDragEvent = (event) => {
  const dt = event.dataTransfer;
  if (!dt) return false;

  // Check for files in types array
  if (dt.types && Array.from(dt.types).includes('Files')) {
    return true;
  }

  // Fallback: check items property
  if (dt.items) {
    return Array.from(dt.items).some(item => item.kind === 'file');
  }

  return false;
};

// Core Interactive Logic
const dropFile = (event) => {
  isDragOver.value = false;
  isFileDraggedGlobally.value = false;
  dragCounter.value = 0;

  event.preventDefault();

  if (!isFileDragEvent(event)) return;

  const rawFiles = [...event.dataTransfer.items]
    .filter(f => f.kind === 'file')
    .map((f) => {
      const file = f.getAsFile();
      const { name } = file;
      const mimeType = parseFileExtension(name);
      return {
        name,
        type: mimeType,
        file,
      };
    });

  files.value = [...files.value, ...rawFiles];
  emitFiles();

  // Reset input value to allow reselection and close file picker if it was open
  if (inputFile.value) {
    inputFile.value.value = '';
  }
};

const openSingleFileDialog = () => {
  if (inputFile.value) {
    inputFile.value.click();
  }
};

const selectSingleFile = (event) => {
  const fileList = [...event.target.files];

  if (props.multiple) {
    const newFiles = fileList.map(file => {
      const { name } = file;
      const mimeType = parseFileExtension(name);
      return {
        name,
        type: mimeType,
        file,
      };
    });
    files.value = [...files.value, ...newFiles];
  } else {
    const file = fileList[0];
    const { name } = file;
    const mimeType = parseFileExtension(name);
    files.value = [
      ...files.value,
      {
        name,
        type: mimeType,
        file,
      },
    ];
  }

  emitFiles();
  // Reset input value to allow reselection and close file picker
  if (inputFile.value) {
    inputFile.value.value = '';
  }
};

const deleteFile = (index) => {
  files.value.splice(index, 1);
  emitFiles();
};

const dragging = (event) => {
  event.preventDefault();
  if (isFileDragEvent(event)) {
    isDragOver.value = true;
  }
};

const dragLeave = (event) => {
  event.preventDefault();
  isDragOver.value = false;
};

// Global drag event handlers
const handleGlobalDragEnter = (event) => {
  event.preventDefault();

  if (!props.showGlobalDropZone || !isFileDragEvent(event)) return;

  dragCounter.value++;
  isFileDraggedGlobally.value = true;
};

const handleGlobalDragOver = (event) => {
  event.preventDefault();

  if (!props.showGlobalDropZone || !isFileDragEvent(event)) return;

  // Keep overlay visible during drag over
  isFileDraggedGlobally.value = true;
  event.dataTransfer.dropEffect = 'copy';
};

const handleGlobalDragLeave = (event) => {
  event.preventDefault();

  dragCounter.value--;

  // Only hide when all nested drag events have left
  if (dragCounter.value === 0) {
    isFileDraggedGlobally.value = false;
  }
};

const handleGlobalDrop = (event) => {
  event.preventDefault();
  dragCounter.value = 0;
  isFileDraggedGlobally.value = false;
};

// Clear handler for standard event bus
const clearFiles = () => {
  files.value = [];
};

// Watchers
watch(() => props.value, (newValue) => {
  if (newValue && props.modelValue.length === 0) {
    files.value = [...newValue];
  }
}, { immediate: true });

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    files.value = [...newValue];
  }
}, { immediate: true });

// Lifecycle hooks
onMounted(() => {
  if (props.bus) {
    props.bus.on("delete", clearFiles);
  }

  // Add global drag listeners
  if (props.showGlobalDropZone) {
    window.addEventListener('dragenter', handleGlobalDragEnter);
    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('dragleave', handleGlobalDragLeave);
    window.addEventListener('drop', handleGlobalDrop);
  }
});

onUnmounted(() => {
  if (props.bus) {
    props.bus.off("delete", clearFiles);
  }

  // Remove global drag listeners
  if (props.showGlobalDropZone) {
    window.removeEventListener('dragenter', handleGlobalDragEnter);
    window.removeEventListener('dragover', handleGlobalDragOver);
    window.removeEventListener('dragleave', handleGlobalDragLeave);
    window.removeEventListener('drop', handleGlobalDrop);
  }
});
</script>

<style lang="scss" scoped>
.drop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-in-out;

  &__content {
    background: var(--bg-1);
    border: 3px dashed var(--primary-color, #22db8e);
    border-radius: var(--radius-2, 12px);
    padding: 60px 40px;
    text-align: center;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    min-width: 300px;
  }

  &__icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
    background: url("../assets/icons/default/Bold 2px-file_add.svg") no-repeat center;
    background-size: contain;
    opacity: 0.8;
  }

  &__text {
    font-weight: 600;
    color: var(--text-2);
    margin: 0;
    font-size: var(--medium);
    font-family: var(--muli-text-semi-bold);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.drop {
  background: var(--bg-3);
  border-radius: var(--radius-1);
  min-width: 228px;
  max-width: 228px;
  min-height: 220px;
  max-height: 220px;
  position: relative;
  padding: 8px;

  &--over {
    background: var(--bg-1);
    box-shadow: 0px 7px 20px var(--shadow-3);

    * {
      pointer-events: none;
    }
  }

  &__icon {
    width: 76px;
    height: 76px;
    position: relative;
    margin: 35px auto;
    pointer-events: none;

    &::after,
    &::before {
      position: absolute;
      content: "";
      display: block;
      height: 100%;
      width: 100%;
    }

    &::after {
      background: url("../assets/icons/default/Bold 2px-arrow_up_down.svg") no-repeat center;
    }

    &::before {
      background: url("../assets/icons/default/Bold 2px-file_add.svg") no-repeat center;
    }
  }

  .files {
    height: 138px;
    max-height: 138px;
    overflow: auto;
    width: 100%;
    background: var(--bg-13);
    border-radius: var(--radius-1);
  }

  .file {
    padding: 10px;
    display: flex;
    align-items: center;

    &__name {
      color: var(--text-2);
      font-size: var(--x-smaller);
      margin: 0 10px;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    &__delete {
      background: url("../assets/icons/default/x_delete_icon.svg") no-repeat;
      min-width: 12px;
      min-height: 12px;
      cursor: pointer;
    }

    &__type {
      min-height: 24px;
      min-width: 24px;

      &--pdf {
        background: url("../assets/icons/file-types/pdf_icon.svg") no-repeat;
      }

      &--image {
        background: url("../assets/icons/file-types/image_icon.svg") no-repeat;
      }

      &--file {
        background: url("../assets/icons/file-types/file_icon.svg") no-repeat;
      }

      &--doc {
        background: url("../assets/icons/file-types/doc_icon.svg") no-repeat;
      }
    }
  }

  &__title {
    color: var(--text-2);
    font-family: var(--muli-text-semi-bold);
    text-align: center;
    margin: 10px 0;
  }

  &__button {
    height: 30px;
    width: 100px;
    font-family: var(--muli-text-regular);
    margin: auto;
  }

  &__title,
  &__button {
    font-size: var(--x-smaller);
  }

  &__input {
    opacity: 0;
    position: absolute;
    left: 0;
    width: 100%;
    z-index: -1;
  }

  &:after {
    position: absolute;
    top: 0;
    left: 0;
    content: "";
    display: block;
    height: 100%;
    width: 100%;
    background: url("../assets/icons/default/dragndrop_border.svg") no-repeat;
    background-position: center;
    background-size: 100% 100%;
    pointer-events: none;
  }
}

@media screen and (max-width: 500px) {
  .drop {
    margin: 0 auto;
    min-width: 220px;
    max-width: 220px;
    min-height: 210px;
    max-height: 210px;
  }
 
  .drop__icon {
    width: 60px;
    height: 60px;
    margin: 30px auto;
  }

  .drop__title {
    margin-bottom: 20px;
  }
}
</style>
